import React from 'react';

// Static print optimization styles moved outside component to prevent re-injection
// NOTE: Ye styles sirf popup window mein inject hote hain (PrintManager.popupPrint)
const printStyles = `
  @page {
      size: A4;
      margin: 8mm;
  }
  @media print {
      html, body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background-color: white !important;
          margin: 0 !important;
          padding: 0 !important;
          min-width: 0 !important;
          min-height: 0 !important;
          overflow: visible !important;
      }
      .invoice-wrapper {
          box-shadow: none !important;
          margin: 0 auto !important;
          width: 210mm !important;
          min-height: 0 !important;
          border: none !important;
      }
      .no-print {
          display: none !important;
      }
  }
`;

// Helper for Indian Number/Currency Formatting (e.g., 1,00,000.00)
const formatINR = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Reusable Invoice Component
const Invoice = ({ data }: { data: any }) => {
  const company = data?.company || {};
  const meta = data?.meta || {};
  const consignee = data?.consignee || {};
  const buyer = data?.buyer || {};
  const items = data?.items || [];
  const totals = data?.totals || {};
  const bank = data?.bank || {};
  const declaration = data?.declaration || '';

  // All borders as inline styles for reliable print/PDF rendering
  const s = {
    box:  { border: '1px solid #000' } as React.CSSProperties,
    b:    { borderBottom: '1px solid #000' } as React.CSSProperties,
    r:    { borderRight: '1px solid #000' } as React.CSSProperties,
    t:    { borderTop: '1px solid #000' } as React.CSSProperties,
    bg:   { backgroundColor: '#f9fafb' } as React.CSSProperties,
  };

  return (
    <>
      <style>{printStyles}</style>

      {/* A4 Size Wrapper */}
      <div
        className="invoice-wrapper mx-auto bg-white shadow-xl"
        style={{ width: '210mm', boxSizing: 'border-box', fontSize: '13px', lineHeight: '1.4', display: 'flex', flexDirection: 'column', padding: '8px', fontFamily: 'Arial, sans-serif', color: '#000' }}
      >
        {/* Main Invoice Box */}
        <div style={{ ...s.box, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>

          {/* Header */}
          <div style={{ ...s.b, ...s.bg, textAlign: 'center', fontWeight: 700, fontSize: '16px', padding: '6px 0', letterSpacing: '2px' }}>
            {meta?.documentTitle || 'TAX INVOICE'}
          </div>

          {/* Company + Invoice Meta */}
          <div style={{ display: 'flex', ...s.b }}>
            <div style={{ width: '50%', padding: '10px 12px', ...s.r }}>
              <div style={{ fontWeight: 700, fontSize: '17px', marginBottom: '4px', textTransform: 'uppercase' }}>{company?.name || ''}</div>
              {(company?.address || []).map((line, i) => <div key={i} style={{ fontSize: '12px' }}>{line}</div>)}
              {company?.phone && <div style={{ fontSize: '12px', marginTop: '4px' }}><span style={{ color: '#666', fontSize: '11px' }}>Phone: </span>{company.phone}</div>}
              {company?.gstin && <div style={{ fontSize: '12px', marginTop: '4px' }}><span style={{ color: '#666', fontSize: '11px' }}>GSTIN/UIN: </span><span style={{ fontWeight: 600 }}>{company.gstin}</span></div>}
              {company?.stateName && <div style={{ fontSize: '12px' }}><span style={{ color: '#666', fontSize: '11px' }}>State Name: </span>{company.stateName}{company?.stateCode && <span>, <span style={{ color: '#666', fontSize: '11px' }}>Code: </span>{company.stateCode}</span>}</div>}
              {company?.email && <div style={{ fontSize: '12px' }}><span style={{ color: '#666', fontSize: '11px' }}>E-Mail: </span>{company.email}</div>}
            </div>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', ...s.b, flex: 1 }}>
                <div style={{ width: '50%', padding: '8px', ...s.r }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Invoice No.</div>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{meta?.invoiceNo || ''}</div>
                </div>
                <div style={{ width: '50%', padding: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Dated</div>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{meta?.date || ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', flex: 1 }}>
                <div style={{ width: '50%', padding: '8px', ...s.r }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Delivery Note</div>
                  <div style={{ fontWeight: 500 }}>{meta?.deliveryNote || '-'}</div>
                </div>
                <div style={{ width: '50%', padding: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Terms of Payment</div>
                  <div style={{ fontWeight: 500 }}>{meta?.termsOfPayment || ''}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Party Details */}
          <div style={{ display: 'flex', ...s.b }}>
            <div style={{ width: '50%', padding: '10px 12px', ...s.r }}>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>Consignee (Ship to)</div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{consignee?.name || ''}</div>
              {(consignee?.address || []).map((line, i) => <div key={i} style={{ fontSize: '12px' }}>{line}</div>)}
              {consignee?.gstin && <div style={{ fontSize: '12px', marginTop: '4px' }}><span style={{ color: '#666', fontSize: '11px' }}>GSTIN/UIN: </span><span style={{ fontWeight: 600 }}>{consignee.gstin}</span></div>}
              {consignee?.stateName && <div style={{ fontSize: '12px' }}><span style={{ color: '#666', fontSize: '11px' }}>State Name: </span>{consignee.stateName}{consignee?.stateCode && <span>, <span style={{ color: '#666', fontSize: '11px' }}>Code: </span>{consignee.stateCode}</span>}</div>}
            </div>
            <div style={{ width: '50%', padding: '10px 12px' }}>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>Buyer (Bill to)</div>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{buyer?.name || ''}</div>
              {(buyer?.address || []).map((line, i) => <div key={i} style={{ fontSize: '12px' }}>{line}</div>)}
              {buyer?.gstin && <div style={{ fontSize: '12px', marginTop: '4px' }}><span style={{ color: '#666', fontSize: '11px' }}>GSTIN/UIN: </span><span style={{ fontWeight: 600 }}>{buyer.gstin}</span></div>}
              {buyer?.stateName && <div style={{ fontSize: '12px' }}><span style={{ color: '#666', fontSize: '11px' }}>State Name: </span>{buyer.stateName}{buyer?.stateCode && <span>, <span style={{ color: '#666', fontSize: '11px' }}>Code: </span>{buyer.stateCode}</span>}</div>}
            </div>
          </div>

          {/* Item Table */}
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {/* Table Header */}
            <div style={{ display: 'flex', ...s.b, ...s.bg, fontWeight: 700, textAlign: 'center', fontSize: '12px' }}>
              <div style={{ width: '6%',  padding: '7px 4px', ...s.r }}>Sl No.</div>
              <div style={{ width: '44%', padding: '7px 8px', ...s.r }}>Description of Goods</div>
              <div style={{ width: '10%', padding: '7px 4px', ...s.r }}>Quantity</div>
              <div style={{ width: '15%', padding: '7px 4px', ...s.r }}>Rate</div>
              <div style={{ width: '7%',  padding: '7px 4px', ...s.r }}>per</div>
              <div style={{ width: '18%', padding: '7px 8px' }}>Amount</div>
            </div>

            {/* Table Body */}
            <div style={{ display: 'flex', flexGrow: 1, position: 'relative', minHeight: '220px', ...s.b }}>
              {/* Column separator lines */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none' }}>
                <div style={{ width: '6%',  ...s.r }} />
                <div style={{ width: '44%', ...s.r }} />
                <div style={{ width: '10%', ...s.r }} />
                <div style={{ width: '15%', ...s.r }} />
                <div style={{ width: '7%',  ...s.r }} />
                <div style={{ width: '18%' }} />
              </div>
              <div style={{ width: '100%', position: 'relative', zIndex: 1, paddingBottom: '12px' }}>
                {items.map((item, index) => (
                  <div key={item?.id || index} style={{ display: 'flex', width: '100%', paddingTop: index === 0 ? '6px' : '12px' }}>
                    <div style={{ width: '6%',  padding: '0 4px', textAlign: 'center' }}>{item?.slNo || ''}</div>
                    <div style={{ width: '44%', padding: '0 10px' }}>
                      <span style={{ fontWeight: 700 }}>{item?.description || ''}</span>
                      {item?.subtitle && <div style={{ color: '#666', fontSize: '11px', fontStyle: 'italic' }}>{item.subtitle}</div>}
                    </div>
                    <div style={{ width: '10%', padding: '0 6px', textAlign: 'right' }}>{item?.quantity || ''}</div>
                    <div style={{ width: '15%', padding: '0 6px', textAlign: 'right' }}>{formatINR(item?.rate)}</div>
                    <div style={{ width: '7%',  padding: '0 4px', textAlign: 'center' }}>{item?.per || ''}</div>
                    <div style={{ width: '18%', padding: '0 10px', textAlign: 'right', fontWeight: 600 }}>{formatINR(item?.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Totals Row */}
          <div style={{ display: 'flex', fontWeight: 700, ...s.bg, ...s.b, fontSize: '13px', alignItems: 'center' }}>
            <div style={{ width: '50%', padding: '7px 12px', textAlign: 'right', ...s.r }}>Sub Total</div>
            <div style={{ width: '10%', padding: '7px 6px', textAlign: 'right', ...s.r }}>{totals?.quantity || ''}</div>
            <div style={{ width: '15%', padding: '7px 6px', ...s.r }}></div>
            <div style={{ width: '7%',  padding: '7px 4px', ...s.r }}></div>
            <div style={{ width: '18%', padding: '7px 10px', textAlign: 'right', fontSize: '14px' }}>{formatINR(totals?.taxableAmount ?? totals?.amount)}</div>
          </div>

          {/* GST Rows */}
          {(totals?.gstRows || []).map((row: any, i: number) => (
            <div key={i} style={{ display: 'flex', ...s.b, fontSize: '12px', alignItems: 'center' }}>
              <div style={{ width: '82%', padding: '5px 12px', textAlign: 'right', ...s.r, fontStyle: 'italic', color: '#444' }}>{row.label}</div>
              <div style={{ width: '18%', padding: '5px 10px', textAlign: 'right', fontWeight: 600 }}>{formatINR(row.amount)}</div>
            </div>
          ))}

          {/* Grand Total Row — only show if GST rows exist */}
          {(totals?.gstRows || []).length > 0 && (
            <div style={{ display: 'flex', fontWeight: 700, ...s.bg, ...s.b, fontSize: '14px', alignItems: 'center' }}>
              <div style={{ width: '82%', padding: '8px 12px', textAlign: 'right', ...s.r }}>Grand Total</div>
              <div style={{ width: '18%', padding: '8px 10px', textAlign: 'right' }}>{formatINR(totals?.amount)}</div>
            </div>
          )}

          {/* Amount in Words */}
          <div style={{ padding: '10px 12px', ...s.b }}>
            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Amount Chargeable (in words)</div>
            <div style={{ fontWeight: 700, fontSize: '14px', marginTop: '2px' }}>{totals?.amountInWords || ''}</div>
          </div>

          {/* Footer: Bank + Signatory */}
          <div style={{ display: 'flex', minHeight: '130px' }}>
            <div style={{ width: '50%', ...s.r, display: 'flex', flexDirection: 'column' }}>
              {(bank?.name || bank?.acNo || bank?.branchIfsc) && (
                <div style={{ padding: '10px 12px', ...s.b, flexGrow: 1 }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '6px' }}>Company's Bank Details</div>
                  <table style={{ fontSize: '12px', borderCollapse: 'collapse' }}>
                    <tbody>
                      {bank?.name && <tr><td style={{ color: '#555', paddingRight: '8px', paddingBottom: '3px' }}>Bank Name</td><td style={{ fontWeight: 700 }}>: {bank.name}</td></tr>}
                      {bank?.acNo && <tr><td style={{ color: '#555', paddingRight: '8px', paddingBottom: '3px' }}>A/c No.</td><td style={{ fontWeight: 700 }}>: {bank.acNo}</td></tr>}
                      {bank?.branchIfsc && <tr><td style={{ color: '#555', paddingRight: '8px' }}>Branch & IFS Code</td><td style={{ fontWeight: 700 }}>: {bank.branchIfsc}</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
              {declaration && (
                <div style={{ padding: '10px 12px', ...s.bg }}>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '3px' }}>Declaration</div>
                  <div style={{ fontSize: '11px', color: '#555', fontStyle: 'italic' }}>{declaration}</div>
                </div>
              )}
            </div>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '10px 12px' }}>
              <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase' }}>for {company?.name || ''}</div>
              <div style={{ height: '60px' }}></div>
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#666', textTransform: 'uppercase', borderTop: '1px solid #ccc', paddingTop: '6px', width: '180px', marginLeft: 'auto' }}>
                Authorised Signatory
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Invoice;
